import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-alert-list',
  templateUrl: './alert-list.component.html',
  styleUrls: ['./alert-list.component.css']
})
export class AlertListComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  alerts: any[] = [];
  loading = false;
  page = 1;
  limit = 20;
  total = 0;
  totalPages = 0;

  // Filters
  statusFilter: string = '';
  alertTypeFilter: string = '';

  // Alert type options
  alertTypes = [
    { value: 'subscription_expiry', label: 'Subscription Expiry' },
    { value: 'boq_bidding_invitation', label: 'BOQ Bidding Invitation' },
    { value: 'boq_bid_received', label: 'BOQ Bid Received' },
    { value: 'boq_bid_approved', label: 'BOQ Bid Approved' },
    { value: 'boq_bid_declined', label: 'BOQ Bid Declined' },
    { value: 'boq_bid_awarded', label: 'BOQ Bid Awarded' },
    { value: 'boq_purchase_order', label: 'Purchase Order' },
    { value: 'material_shortage', label: 'Material Shortage' },
    { value: 'machine_breakdown', label: 'Machine Breakdown' },
    { value: 'invoice_overdue', label: 'Invoice Overdue' },
    { value: 'execution_delay', label: 'Execution Delay' },
    { value: 'deviation_pending', label: 'Deviation Pending' },
    { value: 'wcc_pending', label: 'WCC Pending' },
    { value: 'payment_overdue', label: 'Payment Overdue' }
  ];

  statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'acknowledged', label: 'Acknowledged' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'dismissed', label: 'Dismissed' }
  ];

  constructor(
    private apiService: ApiService,
    private toastrService: ToastrMsgService,
    private router: Router,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadAlerts();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadAlerts(): void {
    this.loading = true;
    const params: any = {
      page: this.page,
      limit: this.limit
    };

    if (this.statusFilter) {
      params.status = this.statusFilter;
    }

    if (this.alertTypeFilter) {
      params.alert_type = this.alertTypeFilter;
    }

    this.apiService.getAlerts(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res.success) {
            this.alerts = res.data;
            this.total = res.pagination.total;
            this.totalPages = res.pagination.totalPages;
          }
          this.loading = false;
        },
        error: (err) => {
          console.error('Error loading alerts:', err);
          this.toastrService.error('Failed to load alerts');
          this.loading = false;
        }
      });
  }

  acknowledgeAlert(alert: any): void {
    this.apiService.acknowledgeAlert(alert.alert_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res.success) {
            this.toastrService.success('Alert acknowledged');
            this.loadAlerts();
          }
        },
        error: (err) => {
          console.error('Error acknowledging alert:', err);
          const errorMsg = err.error?.message || 'Failed to acknowledge alert';
          this.toastrService.error(errorMsg);
        }
      });
  }

  dismissAlert(alert: any): void {
    this.apiService.dismissAlert(alert.alert_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res.success) {
            this.toastrService.success('Alert dismissed');
            this.loadAlerts();
          }
        },
        error: (err) => {
          console.error('Error dismissing alert:', err);
          this.toastrService.error('Failed to dismiss alert');
        }
      });
  }

  getSeverityClass(severity: string): string {
    const classes: { [key: string]: string } = {
      low: 'severity-low',
      medium: 'severity-medium',
      high: 'severity-high',
      critical: 'severity-critical'
    };
    return classes[severity] || 'severity-medium';
  }

  getSeverityIcon(severity: string): string {
    const icons: { [key: string]: string } = {
      low: 'info',
      medium: 'warning',
      high: 'error',
      critical: 'error'
    };
    return icons[severity] || 'warning';
  }

  getAlertTypeLabel(type: string): string {
    const alertType = this.alertTypes.find(t => t.value === type);
    return alertType ? alertType.label : type;
  }

  getBiddingLink(alert: any): string | null {
    return alert?.metadata?.bidding_link || null;
  }

  getInvitationId(alert: any): number | null {
    return alert?.metadata?.invitation_id ?? null;
  }

  viewBids(alert: any): void {
    const invitationId = this.getInvitationId(alert);
    if (invitationId) {
      this.router.navigate(['/home/boq-bidding/view-bids', invitationId]);
    }
  }

  viewAwardedProjectWbs(alert: any): void {
    const projectId = alert?.metadata?.project_id;
    if (projectId) {
      this.router.navigate(['/home/work-breakdown-structure', projectId], {
        queryParams: { bidderView: 'true' }
      });
    }
  }

  viewPurchaseOrder(alert: any): void {
    const poId = alert?.metadata?.po_id || alert?.entity_id;
    if (poId) {
      this.router.navigate(['/home/received-purchase-orders', poId]);
    }
  }

  viewAndSubmitBid(alert: any): void {
    const link = this.getBiddingLink(alert);
    const token = alert?.metadata?.token;
    if (link) {
      try {
        const url = new URL(link);
        this.router.navigateByUrl(url.pathname || `/bidding/invite/${token || ''}`);
      } catch {
        if (token) this.router.navigate(['/bidding/invite', token]);
      }
    } else if (token) {
      this.router.navigate(['/bidding/invite', token]);
    }
  }

  onPageChange(page: number): void {
    this.page = page;
    this.loadAlerts();
  }

  onFilterChange(): void {
    this.page = 1;
    this.loadAlerts();
  }

  clearFilters(): void {
    this.statusFilter = '';
    this.alertTypeFilter = '';
    this.page = 1;
    this.loadAlerts();
  }
}
